import React from 'react';
import { PropTypes as PT } from 'prop-types';
import clipboard from 'clipboard-js';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import { saveAs } from 'file-saver';
import ICalEvent from 'icalevent';
import SVG from 'react-inlinesvg';
import calendarIcon from '@lifesize/ux-assets/vectors/svgIcons/calendar.svg';
import clipboardIcon from '@lifesize/ux-assets/vectors/svgIcons/clipboard.svg';
import emailIcon from '@lifesize/ux-assets/vectors/svgIcons/email.svg';
import Tooltip from 'components/Tooltip/Tooltip';
import Button from 'components/Button/Button';
import { getDomain } from 'utils/UrlUtil';
import lsConfig from 'utils/global-config';
import logger from 'utils/logger';
import classes from './ScheduleInfo.module.scss';

const messages = defineMessages({
  copy: {
    id: 'components.scheduleModal.clipboard.copy',
    defaultMessage: 'Copy'
  },
  copied: {
    id: 'components.scheduleModal.clipboard.copied',
    defaultMessage: 'Copied!'
  },
  copiedNotification: {
    id: 'components.scheduleModal.clipboard.copiedNotification',
    defaultMessage: 'The invitation has been copied to your clipboard.'
  },
  inviteTitle: {
    id: 'components.scheduleModal.invite.title',
    defaultMessage: 'lifesize_invite.ics'
  }
});

let invitationPromise;
let mailtoPromise;

class ScheduleInfo extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isCopied: false
    };
  }

  componentDidMount = () => {
    invitationPromise = this.props.getInviteForExtension();
    mailtoPromise = this.props.getInviteMailLinkForExtension();
  };

  copyInviteText = async () => {
    try {
      const invitation = await invitationPromise;
      clipboard.copy({ 'text/plain': invitation.body });
      // trigger on-screen notification
      this.props.notifyWithTimeout(this.props.intl.formatMessage(messages.copiedNotification));
    } catch (e) {
      logger.error(`Unable to copy invitation: ${e}`);
    }
  };

  copyLinkText = () => {
    clipboard.copy({ 'text/plain': this.props.contact.inviteURL });

    this.setState({
      isCopied: true
    });
  };

  handleCalendarClick = async () => {
    let event;
    try {
      const invitation = await invitationPromise;
      const today = new Date();
      event = new ICalEvent({
        uid: `${this.props.myInfo.id}${today.toISOString()}@${getDomain()}`,
        dtstamp: today,
        offset: new Date().getTimezoneOffset(),
        summary: invitation.subject,
        description: invitation.body.replace(/\n/g, '\\n'),
        location: this.props.contact.inviteURL,
        start: new Date(today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          today.getHours() + 1, 0, 0),
        end: new Date(today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          today.getHours() + 2, 0, 0)
      });
    } catch (e) {
      logger.error(`Unable to get invitation: ${e}`);
      return;
    }

    // Download the iCal event
    const inviteTitle = this.props.intl.formatMessage(messages.inviteTitle);

    if (window.desktopApp) {
      window.desktopApp.launchTempFile(inviteTitle, event.toFile());
      return;
    }

    const blob = new Blob([event.toFile()], { type: 'text/calendar;charset=utf-8' });

    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, inviteTitle);
      return;
    }

    const element = document.createElement('a');

    element.setAttribute('href', `data:text/calendar;charset=utf-8,${encodeURIComponent(event.toFile())}`);
    element.setAttribute('download', inviteTitle);

    element.style.display = 'none';
    document.body.appendChild(element);

    lsConfig.skipOnBeforeUnload();

    try {
      element.click();
    } catch (e) {
      // Some browsers have a max file length for href. If so, we use file-saver as a fallback.
      saveAs(blob, inviteTitle);
    }

    document.body.removeChild(element);
  };

  handleEmailClick = async () => {
    try {
      const mailto = await mailtoPromise;
      // Create and click email link
      const element = document.createElement('a');
      element.setAttribute('href', mailto);

      element.style.display = 'none';
      document.body.appendChild(element);

      lsConfig.skipTimeOnBeforeUnload(1000);
      element.click();

      document.body.removeChild(element);
      // blur the button so that it is no longer the activeElement (so onBeforeUnload is again active)
      document.getElementById('btnSendScheduleEmail').blur();
    } catch (err) {
      logger.error(`Unable to create mailto link: ${err}`);
    }
  };

  copyLinkSection = () => {
    const copyStatus = this.state.isCopied
      ? this.props.intl.formatMessage(messages.copied)
      : this.props.intl.formatMessage(messages.copy);

    return (
      <div className={classes.callLinkContainer}>
        <div className={classes.label}>
          <FormattedMessage
            id="components.scheduleModal.meetingLinkLabel"
            defaultMessage="Meeting Link"
          />
        </div>
        <div className={classes.link} >
          {this.props.contact.inviteURL}
          <button onClick={this.copyLinkText} className={`${classes.copyStatus} ${this.state.isCopied ? classes.copied : ''} secretButton`} data-testid='copy-meeting-link'>
            {copyStatus}
          </button>
        </div>
      </div>);
  };

  render() {
    return (
      <section className={classes.scheduleInfoContainer}>
        <div className={classes.scheduleInfoHeader}>
          {
            (this.props.myInfo && this.props.contact.id === this.props.myInfo.id) ?
              <FormattedMessage
                id="schedule.info.selfTitle"
                defaultMessage="Share my invitation"
              />
              :
              <FormattedMessage
                id="schedule.info.title"
                defaultMessage="Share {name}'s invitation"
                values={{ name: this.props.contact.name }}
              />
          }
        </div>
        <div className={classes.buttonBar}>
          <Button
            dataAttrs={{ 'data-onbeforeunload': 'ignore', 'data-testid': 'schedule-email' }}
            id="btnSendScheduleEmail"
            onClick={this.handleEmailClick}
            style={{ minWidth: '150px', padding: 10, margin: '0 5px' }}
          >
            <SVG src={emailIcon} />
            <FormattedMessage id="components.scheduleModal.emailLabel" defaultMessage="Email" />
            <Tooltip
              label={<FormattedMessage id="components.scheduleModal.emailTooltip" defaultMessage="Share by email" />}
              style={{ marginTop: 7, minWidth: 'auto', top: '100%' }}
            />
          </Button>
          <Button
            dataAttrs={{ 'data-testid': 'schedule-calendar' }}
            onClick={this.handleCalendarClick}
            style={{ minWidth: 'auto', padding: 10, margin: '0 5px' }}
          >
            <SVG src={calendarIcon} />
            <FormattedMessage id="components.scheduleModal.calendarLabel" defaultMessage="Calendar" />
            <Tooltip
              label={<FormattedMessage id="components.scheduleModal.calendarTooltip" defaultMessage="Create event" />}
              style={{ marginTop: 7, minWidth: 'auto', top: '100%' }}
            />
          </Button>
          <Button
            dataAttrs={{ 'data-testid': 'schedule-clipboard' }}
            onClick={this.copyInviteText}
            style={{ minWidth: 'auto', padding: 10, margin: '0 5px' }}
          >
            <SVG src={clipboardIcon} />
            <FormattedMessage id="components.scheduleModal.clipboard" defaultMessage="Clipboard" />
            <Tooltip
              label={
                <FormattedMessage
                  id="components.scheduleModal.tooltip.clipboard"
                  defaultMessage="Copy to clipboard"
                />}
              style={{ marginTop: 7, minWidth: 'auto', top: '100%', margin: '0 5px' }}
            />
          </Button>
        </div>
        {this.props.contact.inviteURL && this.copyLinkSection()}
      </section>
    );
  }
}

ScheduleInfo.propTypes = {
  myInfo: PT.object,
  contact: PT.object,
  intl: PT.object,
  notifyWithTimeout: PT.func,
  getInviteForExtension: PT.func,
  getInviteMailLinkForExtension: PT.func
};

export default injectIntl(ScheduleInfo);
